// src/lib/use-pricing-blocks-history.js — PR #169b (ADR-106)
//
// Hook React qui charge l'historique COMPLET des pricing_blocks
// "résa direct interne" (guest_first_name NOT NULL AND total_amount NOT NULL
// AND active=true) pour alimenter le wrapper `calc` enrichi de App.jsx.
//
// Indexation : Map<monthKey "YYYY-MM", Map<logement_id, Array<block>>>.
// Un bloc multi-mois est indexé dans UN SEUL monthkey via la règle ADR-049
// "majorité de nuits" (getResaInvoiceMonth). Anti-double-comptage H-1
// (pessimist QA pre-code PR #169b v1.1).
//
// Différence vs use-daily-pricing.js (ne pas confondre) :
//   - use-daily-pricing : range 18 mois pour calendrier visuel
//   - use-pricing-blocks-history : historique PLURIANNUEL pour Compta / KPIs.
//     Volumétrie attendue : qq dizaines de blocs (faible).
//
// Filtre serveur strict (H-2) :
//   - .not('guest_first_name', 'is', null)   → uniquement résas voyageur
//   - .not('total_amount', 'is', null)        → uniquement avec CA renseigné
//   - .eq('active', true)                     → soft-delete respect
//
// API exposée :
//   { blocksByMonth: Map, loading, error, invalidate: () => refetch }
//
// Pattern lifting App.jsx (cf. F1 audit) : un seul fetch global au mount,
// invalidate() exposé en prop à ResaV2Modal pour refresh post-mutation.

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabase'
import { getResaInvoiceMonth } from './calc'

const EMPTY_MAP = new Map()

/**
 * Charge tous les pricing_blocks "résa direct interne" + indexe par mois × logement.
 *
 * @param {boolean} enabled — si false, hook ne fetch pas (utile si pas d'admin auth)
 * @returns {{ blocksByMonth: Map, loading: boolean, error: string|null, invalidate: () => void }}
 */
export function usePricingBlocksHistory(enabled = true) {
  const [state, setState] = useState({
    blocksByMonth: EMPTY_MAP,
    loading: false,
    error: null,
  })
  const [refetchTick, setRefetchTick] = useState(0)

  useEffect(() => {
    if (!enabled) {
      setState({ blocksByMonth: EMPTY_MAP, loading: false, error: null })
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))

    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('pricing_blocks')
          .select('id, logement_id, start_date, end_date, total_amount, guest_first_name, guest_last_name, reason, active')
          .not('guest_first_name', 'is', null)
          .not('total_amount', 'is', null)
          .eq('active', true)

        if (cancelled) return
        if (error) {
          console.error('[usePricingBlocksHistory] supabase error:', error)
          setState({ blocksByMonth: EMPTY_MAP, loading: false, error: error.message })
          return
        }

        // ── Lot « Taxe directe » (Voie A) : enrichir b.taxe_sejour ──
        // Symétrie admin↔proprio (cf. lib/proprio-handlers.js). La taxe figée
        // d'une résa directe vit dans direct_booking_requests.tarif_snapshot_breakdown
        // ->>'taxe_sejour'. Pas de FK pricing_blocks.id ↔ direct_booking_requests
        // .pricing_block_id → jointure embarquée impossible → 2e SELECT borné aux
        // ids des blocs. ANTI-PII STRICT : on ne lit QUE pricing_block_id +
        // tarif_snapshot_breakdown, on n'extrait QUE taxe_sejour, JAMAIS guest_*.
        // Bloc manuel admin (sans request) → absent de la Map → taxe 0.
        const blocks = data || []
        const blockIds = blocks.map((b) => b.id).filter(Boolean)
        const taxeByBlockId = new Map()
        if (blockIds.length) {
          const { data: taxeRows, error: taxeError } = await supabase
            .from('direct_booking_requests')
            .select('pricing_block_id, tarif_snapshot_breakdown')
            .in('pricing_block_id', blockIds)
          if (cancelled) return
          if (taxeError) {
            // Fail-soft : la taxe dégrade à 0, l'historique des blocs reste exploité.
            console.error('[usePricingBlocksHistory] taxe sejour error:', taxeError)
          } else {
            for (const row of taxeRows || []) {
              if (!row || !row.pricing_block_id) continue
              const breakdown = row.tarif_snapshot_breakdown
              const taxe =
                breakdown && breakdown.taxe_sejour != null ? Number(breakdown.taxe_sejour) : 0
              taxeByBlockId.set(row.pricing_block_id, Number.isFinite(taxe) ? taxe : 0)
            }
          }
        }

        // Indexation Map<mk, Map<lid, Array<block>>>
        // Règle ADR-049 "majorité de nuits" appliquée via getResaInvoiceMonth
        // → 1 bloc multi-mois indexé dans 1 SEUL monthkey (H-1 anti-double-comptage).
        const map = new Map()
        for (const b of blocks) {
          b.taxe_sejour = Number(taxeByBlockId.get(b.id)) || 0
          const mk = getResaInvoiceMonth(b.start_date, b.end_date)
          if (!mk) continue
          if (!map.has(mk)) map.set(mk, new Map())
          const monthMap = map.get(mk)
          if (!monthMap.has(b.logement_id)) monthMap.set(b.logement_id, [])
          monthMap.get(b.logement_id).push(b)
        }
        setState({ blocksByMonth: map, loading: false, error: null })
      } catch (e) {
        if (cancelled) return
        console.error('[usePricingBlocksHistory] unexpected:', e)
        setState({ blocksByMonth: EMPTY_MAP, loading: false, error: e.message || String(e) })
      }
    })()

    return () => { cancelled = true }
  }, [enabled, refetchTick])

  const invalidate = useCallback(() => {
    setRefetchTick((t) => t + 1)
  }, [])

  return { ...state, invalidate }
}
